<template>
  <div class="pfol row">
    <div class="pfol__header small-12">
      {{ $t('tenantProjects.comments') }}
    </div>
    <div class="small-12">
      <div class="pfol__body">
        <template v-if="hasUnderReviewPostFitout">
          <div
            class="pfol__title-box"
            :class="[isOverTwoText ? 'align-top' : 'align-middle']"
          >
            <div
              class="pfol__title align-middle"
            >
              {{ underReviewTitle }}
              <DottedMenu
                v-if="showCancelMenu"
                ref="cancelDottedMenu"
                :enableClickOutClose="true"
                color="darkblue"
              >
                <HKLButton
                  action="cancel"
                  @click="openCancelModal"
                />
              </DottedMenu>
            </div>
            <div
              class="tip"
            >
              <HKLGrayHint
                v-for="(item, index) in textHistories"
                :key="index"
              >
                {{ item }}
              </HKLGrayHint>
            </div>
          </div>
          <div class="pfol__content-box row">
            <template v-if="shouldShowDescription">
              <div class="pfol__description-box small-12">
                <DescriptionTinymceEditor
                  v-if="allowedEditingWithPaneActivated"
                  v-model="openingDescription"
                />
                <p
                  v-else
                  class="pfol__description-readonly"
                  v-html="openingDescription"
                />
              </div>
              <div class="pfol__description-box small-12">
                <DescriptionTinymceEditor
                  v-if="allowedEditingWithPaneActivated"
                  v-model="description"
                  :height="150"
                />
                <p
                  v-else
                  class="pfol__description-readonly"
                  v-html="description"
                />
              </div>
              <PostFitOutGuidelineComment
                ref="refPostFitOutGuidelines"
                class="pfol__guide-comment"
              />
              <div class="pfol__description-box small-12">
                <DescriptionTinymceEditor
                  v-if="allowedEditingWithPaneActivated"
                  v-model="closingDescription"
                />
                <p
                  v-else
                  class="pfol__description-readonly"
                  v-html="closingDescription"
                />
              </div>
              <div class="pfol__attachments small-12">
                <div class="row">
                  <HkldDropzone
                    v-if="curReviewerWithAccess"
                    class="small-12"
                    :sourceId="currentSubmission.id"
                  />
                  <TenantSubmissionDocuments
                    class="small-12"
                    :canEdit="curReviewerWithAccess"
                    :sourceId="currentSubmission.id"
                  />
                </div>
              </div>
            </template>
            <div
              v-else
              class="small-12 tip under-review__tip"
            >
              <div class="tip__text">
                {{ underReviewText }}
              </div>
            </div>
          </div>
        </template>
        <PostFitOutHistory />
      </div>
    </div>
    <HKLModal
      ref="cancelModalRef"
      okBtnAction="yes"
      cancelBtnAction="no"
      :title="$t('tenantProjects.postFitout.cancelMsg')"
      @onOkClick="cancel"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { computed, getCurrentInstance, reactive, ref, unref, inject } from '@vue/composition-api'
import HKLGrayHint from '@/controls/HKLGrayHint'
import HkldDropzone from '@/controls/HkldDropzone'
import HKLModal from '@/controls/Modal/HKLModal'
import HKLButton from '@/controls/Button/HKLButton'
import DottedMenu from '@/controls/DottedMenu'
import PostFitOutHistory from './PostFitOutHistory'
import PostFitOutGuidelineComment from './PostFitOutGuidelineComment'
import TenantSubmissionDocuments from '../submissions/TenantSubmissionDocuments'
import DescriptionTinymceEditor from '../DescriptionTinymceEditor.component'
import { useComputedDescription, isCurrentReviewer, useUnderReviewPostFitout, useProjectId, curReviewerWithAccess, hasUnderReviewPostFitout, hasPreviewAccess, hasSendAccess, hasCommentTechAccess } from './hook'
import dateUtil from '@/utils/dateUtil'
import { getLastReviewText, getUnderReviewText } from './util'
import { deletePostFitoutSubmission } from '@/api/tenantProjects/postFitout.request'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { useRefDottedMenu } from '@/controls/dottedMenu.hook'
import { useStoreActions } from '@/store'
import { DEPENDENCY_KEYS, TAB_TYPE } from '../consts'

export default {
  components: {
    HkldDropzone,
    HKLModal,
    HKLButton,
    HKLGrayHint,
    PostFitOutHistory,
    PostFitOutGuidelineComment,
    DescriptionTinymceEditor,
    DottedMenu,
    TenantSubmissionDocuments,
  },
  setup () {
    const shouldShowDescription = computed(() => {
      return unref(curReviewerWithAccess) || unref(hasSendAccess) || unref(hasPreviewAccess) || unref(hasCommentTechAccess)
    })
    const currentSubmission = useUnderReviewPostFitout()
    const openingDescription = useComputedDescription(currentSubmission, 'openingDescription')
    const closingDescription = useComputedDescription(currentSubmission, 'closingDescription')
    const description = useComputedDescription(currentSubmission, 'description')
    const projectId = useProjectId()
    const { proxy } = getCurrentInstance()
    const startOnText = computed(() => {
      const date = _.get(unref(currentSubmission), 'createdAt', null)
      return proxy.$t('tenantProjects.submission.startedOn', [dateUtil.format(date, dateUtil.DD_MMM_YY_HH_MM)])
    })
    const lastReviewText = computed(() => getLastReviewText(unref(currentSubmission)))
    const underReviewText = computed(() => getUnderReviewText(unref(currentSubmission)))
    const textHistoriesMap = reactive({
      underReviewText,
      lastReviewText,
      startOnText,
    })
    const textHistories = computed(() => {
      if (unref(shouldShowDescription)) {
        return Object.values(textHistoriesMap)
      } else {
        return [unref(startOnText)]
      }
    })
    const isOverTwoText = computed(() => unref(textHistories).length >= 2)
    const refPostFitOutGuidelines = ref(null)
    const getRelatedPendingComments = () => {
      const { pendingGuidelines, addHistoryList, removeHistoryList } = unref(refPostFitOutGuidelines).getRelatedPendingGuidelines()
      return {
        comments: {
          description: unref(description),
          openingDescription: unref(openingDescription),
          closingDescription: unref(closingDescription),
          guidelines: Object.values(pendingGuidelines),
        },
        historyList: {
          addHistoryList,
          removeHistoryList,
        },
      }
    }
    const activeTabType = inject(DEPENDENCY_KEYS.TAB_TYPE)
    // There is a bug, when the pane is deactivated, the iframe content is removed.
    // It make the TinymceEditor not work.
    // Use `v-if` to force re-rendering the TinymceEditor
    const allowedEditingWithPaneActivated = computed(() => {
      return unref(curReviewerWithAccess) && unref(activeTabType) === TAB_TYPE.POST_FIT_OUT
    })
    const { loadProjectPostFitoutSubmissions } = useStoreActions('postFitoutSubmission', ['loadProjectPostFitoutSubmissions'])
    const cancelModalRef = useRefModal()
    const cancelDottedMenuHook = useRefDottedMenu()
    const openCancelModal = () => {
      cancelModalRef.open()
      cancelDottedMenuHook.close()
    }
    const cancel = async () => {
      await deletePostFitoutSubmission(unref(currentSubmission).id)
      await loadProjectPostFitoutSubmissions(unref(projectId))
    }
    const underReviewTitle = computed(() => {
      const order = unref(currentSubmission).order
      const number = order ? order + 1 : ''
      return `${proxy.$t('tenantProjects.postFitout.title')} ${number}`
    })
    const showCancelMenu = computed(() => {
      return unref(isCurrentReviewer) && unref(hasCommentTechAccess)
    })
    return {
      underReviewTitle,
      showCancelMenu,
      cancelModalRef: cancelModalRef.modal,
      openCancelModal,
      cancel,
      isCurrentReviewer,
      hasUnderReviewPostFitout,
      shouldShowDescription,
      curReviewerWithAccess,
      allowedEditingWithPaneActivated,
      refPostFitOutGuidelines,
      isOverTwoText,
      underReviewText,
      textHistories,
      closingDescription,
      description,
      openingDescription,
      currentSubmission,
      getRelatedPendingComments,
      cancelDottedMenu: cancelDottedMenuHook.dottedMenu,
    }
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
.pfol {
  &__title-box {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    .hkl-gray-hint {
      font-size: 11px;
    }
  }
  &__title {
    display: flex;
    color: #0c3a58;
    font-size: 14px;
    font-weight: bold;
    ::v-deep {
      .menu-slot .btn {
        text-align: center;
      }
    }
  }
  &__description-box {
    margin-bottom: 5px;
  }
  &__history {
    margin-top: 10px;
  }
  .under-review__tip {
    text-align: center;
    .tip__text {
      font-style: normal;
    }
  }
  &__guide-comment {
    margin-top: 10px;
  }
  &__attachments {
    opacity: 0.9;
    border: solid 1px #e5e9eb;
    padding: 10px 15px 0px 15px;
  }
}
</style>
